import React from 'react'
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
  Tooltip,
  useTheme
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { LocalHospital, HealthAndSafety, OpenInFull } from '@mui/icons-material'
import TeamImage from 'components/shared-components/TeamImage'
import LazyImage from 'components/shared-components/LazyImage'
import { useQuery } from '@tanstack/react-query'
import { ApiQueryKeys, ApiQueryService } from 'services/query/apiQueryService'
import type YahooFantasyPlayerStatistics from 'features/fantasy-team/models/yahooFantasyPlayerStatistics'
import type { ConnectedYahooFantasyPlayer } from 'features/fantasy-team/models/yahooConnectedTeam'
import PropTypes from 'prop-types'

interface PlayerCardProps {
  player: ConnectedYahooFantasyPlayer
  onView: () => void
}

const clamp = (value: number, min = 0, max = 100): number => Math.min(max, Math.max(min, value))

const Sparkline: React.FC<{ playerId: number, color?: string }> = React.memo(({ playerId, color }) => {
  const theme = useTheme()
  const c = color ?? theme.palette.primary.main
  const gradientId = React.useId().replace(/:/g, '')

  const { data } = useQuery<YahooFantasyPlayerStatistics>({
    queryKey: [ApiQueryKeys.GetYahooFantasyPlayerStatistics, playerId],
    queryFn: async () => (await ApiQueryService[ApiQueryKeys.GetYahooFantasyPlayerStatistics](playerId)).data,
    enabled: playerId > 0
  })

  const points = React.useMemo(() => (data?.last5Games ?? []).map(g => g.points).slice(-5), [data?.last5Games])

  const { path, areaPath, avg, hasData } = React.useMemo(() => {
    const xs = points ?? []
    if (xs.length === 0) {
      return { path: '', areaPath: '', avg: 0, hasData: false }
    }

    const w = 260
    const h = 64
    const pad = 6
    const pmin = Math.min(...xs)
    const pmax = Math.max(...xs)
    const delta = pmax - pmin
    const range = Number.isFinite(delta) && delta !== 0 ? delta : 1
    const step = xs.length > 1 ? (w - pad * 2) / (xs.length - 1) : 0
    const y = (v: number): number => h - pad - ((v - pmin) / range) * (h - pad * 2)
    const x = (i: number): number => pad + step * i
    const d = xs.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(2)} ${y(v).toFixed(2)}`).join(' ')
    const area = `${d} L ${(x(xs.length - 1)).toFixed(2)} ${(h - pad).toFixed(2)} L ${(x(0)).toFixed(2)} ${(h - pad).toFixed(2)} Z`
    const avgVal = xs.reduce((s, v) => s + v, 0) / xs.length
    return { path: d, areaPath: area, avg: avgVal, hasData: true }
  }, [points])

  if (!hasData) {
    return (
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.76rem', mt: 0.75 }}>
        Recent trend unavailable.
      </Typography>
    )
  }

  return (
    <Box aria-hidden="true" sx={{ width: '100%', mt: 0.75 }}>
      <Box component="svg" width="100%" height="64" viewBox="0 0 260 64" role="img" preserveAspectRatio="none">
        <title>Last 5 games trend</title>
        <desc>Sparkline of fantasy points over the last five games</desc>
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={c} stopOpacity="0.28" />
            <stop offset="100%" stopColor={c} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#${gradientId})`} />
        <path d={path} fill="none" stroke={c} strokeWidth="2" vectorEffect="non-scaling-stroke" />
      </Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.15 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.76rem' }}>
          Last 5 games
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.76rem', fontWeight: 700 }}>
          Avg: {avg.toFixed(1)} pts
        </Typography>
      </Stack>
    </Box>
  )
})
Sparkline.displayName = 'Sparkline'
Sparkline.propTypes = {
  playerId: PropTypes.number.isRequired,
  color: PropTypes.string
}

const PlayerCard: React.FC<PlayerCardProps> = ({ player, onView }) => {
  const theme = useTheme()

  const { data: fhScore } = useQuery<number>({
    queryKey: [ApiQueryKeys.GetPlayerFantaHockeyScore, player.playerId],
    queryFn: async () => (await ApiQueryService[ApiQueryKeys.GetPlayerFantaHockeyScore](player.playerId)).data,
    enabled: player.playerId > 0
  })

  const fantasyScore = Number.isFinite(player.fantasyScore) ? player.fantasyScore : 0
  const fantaHockeyScore = typeof fhScore === 'number' && Number.isFinite(fhScore) ? fhScore : 0
  const formMeter = clamp(((fantasyScore + fantaHockeyScore) / 2) * 3)
  const teamLabel = player.team.trim() !== '' ? player.team : 'NHL'
  const injuryLabel = player.injuryStatus?.trim() !== '' ? player.injuryStatus : 'Injured'

  return (
    <Paper
      role="group"
      aria-label={`${player.name} card`}
      tabIndex={0}
      dir="auto"
      onKeyDown={(e) => {
        if ((e.target as HTMLElement).closest('button') !== null) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onView()
        }
      }}
      elevation={4}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        p: { xs: 1.5, sm: 2 },
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 1.1, sm: 1.25 },
        borderRadius: 3.5,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
        background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.09)} 0%, ${theme.palette.background.paper} 62%)`,
        boxShadow: `0 14px 34px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.42 : 0.16)}`,
        transition: 'transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          insetInlineEnd: -42,
          top: -56,
          width: 148,
          height: 148,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.info.main, 0.28)} 0%, ${alpha(theme.palette.info.main, 0)} 72%)`,
          pointerEvents: 'none'
        },
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: `0 18px 40px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.5 : 0.2)}`,
          borderColor: alpha(theme.palette.primary.main, 0.34)
        },
        '&:active': { transform: 'translateY(0px) scale(0.997)' },
        outline: 'none',
        '&:focus-visible': {
          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.44)}, 0 18px 40px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.5 : 0.2)}`
        },
        maxWidth: '100%'
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.1, sm: 1.4 }} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
        <Box sx={{ position: 'relative', alignSelf: { xs: 'center', sm: 'flex-start' }, mt: { xs: 0.2, sm: 0 } }}>
          <Box
            sx={{
              width: { xs: 90, sm: 98 },
              height: { xs: 90, sm: 98 },
              p: 0.5,
              borderRadius: '50%',
              background: `linear-gradient(150deg, ${alpha(theme.palette.primary.main, 0.34)} 0%, ${alpha(theme.palette.info.main, 0.34)} 100%)`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.2)}`
            }}
          >
            <LazyImage src={player.playerHeadshotUrl} alt={`${player.name} headshot`} loading="lazy" effect="opacity" height="100%" width="100%" borderRadius="50%" />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              insetInlineStart: -8,
              bottom: -4,
              p: 0.25,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.background.paper, 0.9),
              border: `1px solid ${alpha(theme.palette.divider, 0.7)}`
            }}
          >
            <TeamImage src={player.teamLogoUrl} alt={`${player.team} logo`} height={30} width={30} />
          </Box>
        </Box>

        <Stack direction="column" sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.02rem', sm: '1.12rem' },
                  lineHeight: 1.2,
                  letterSpacing: 0.1
                }}
                title={player.name}
                noWrap
              >
                {player.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.86rem' } }}>
                {teamLabel} • {player.position}
              </Typography>
            </Box>
            <Tooltip title={player.isInjured ? `${player.name} is injured` : `${player.name} is healthy`} aria-label="health-status">
              <Box sx={{ mt: 0.25 }}>
                {player.isInjured ? <LocalHospital color="error" fontSize="medium" /> : <HealthAndSafety color="success" fontSize="medium" />}
              </Box>
            </Tooltip>
          </Stack>

          <Stack direction="row" spacing={0.8} sx={{ mt: 0.8, flexWrap: 'wrap', rowGap: 0.7 }}>
            <Chip
              size="small"
              label={player.isInjured ? `Status: ${injuryLabel}` : 'Status: Healthy'}
              color={player.isInjured ? 'error' : 'success'}
              variant={player.isInjured ? 'filled' : 'outlined'}
              sx={{ height: 24, fontSize: '0.74rem', fontWeight: 700 }}
            />
          </Stack>

          <Box
            sx={{
              mt: 1,
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 1
            }}
          >
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.primary.main, 0.11),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.74rem', display: 'block' }}>
                Yahoo! Fantasy Points
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                {fantasyScore.toFixed(2)}
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.info.main, 0.11),
                border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.74rem', display: 'block' }}>
                FantaHockey Score
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                {fantaHockeyScore.toFixed(2)}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              mt: 0.9,
              height: 7,
              width: '100%',
              borderRadius: 99,
              bgcolor: alpha(theme.palette.text.primary, 0.12),
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${formMeter}%`,
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.info.main} 100%)`,
                transition: 'width 300ms ease'
              }}
            />
          </Box>
        </Stack>
      </Stack>

      <Sparkline playerId={player.playerId} />

      <Divider sx={{ opacity: 0.8 }} />

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: -0.35 }}>
        <Tooltip title="View details">
          <IconButton
            size="large"
            sx={{
              minWidth: 46,
              minHeight: 46,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
            }}
            color="primary"
            aria-label={`view ${player.name}`}
            onClick={onView}
          >
            <OpenInFull />
          </IconButton>
        </Tooltip>
      </Stack>
    </Paper>
  )
}

PlayerCard.displayName = 'PlayerCard'
export default React.memo(PlayerCard)
